import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal, unstable_batchedUpdates } from "react-dom";
import {
  CancelDrop,
  closestCenter,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
  DndContext,
  DragOverlay,
  DropAnimation,
  getFirstCollision,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  Modifiers,
  useDroppable,
  UniqueIdentifier,
  useSensors,
  useSensor,
  MeasuringStrategy,
  KeyboardCoordinateGetter,
  defaultDropAnimationSideEffects,
} from "@dnd-kit/core";
import {
  AnimateLayoutChanges,
  SortableContext,
  useSortable,
  arrayMove,
  defaultAnimateLayoutChanges,
  verticalListSortingStrategy,
  SortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Box } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { coordinateGetter as multipleContainersCoordinateGetter } from "./multipleContainersKeyboardCoordinates";
import { contruirKeysFromData, keysToData } from "../utils/funcionesiIndexTablero";
import { Page } from "components";
import serviciosTablero from "utils/serviciosTablero";

import { Container } from ".";
import { Item } from ".";

import { createRange } from "../utils/createRange";

const animateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

function DroppableContainer({
  children,
  columns = 1,
  disabled,
  id,
  items,
  style,
  dataEtapa,
  dataGeneral,
  infoTablero,
  setData,
  setItems,
  handleRemoveColumn,
  setCargando,
  setMensaje,
  updateData,
  idjerarquia,
  usuario,
  permisos,
  esPrimeraEtapa,
  ...props
}) {
  const {
    active,
    attributes,
    isDragging,
    listeners,
    over,
    setNodeRef,
    transition,
    transform,
  } = useSortable({
    id,
    data: {
      type: "container",
      children: items,
    },
    animateLayoutChanges,
  });
  const isOverContainer = over
    ? (id === over.id && active?.data.current?.type !== "container") ||
      items.includes(over.id)
    : false;

  return (
    <Container
      ref={disabled ? undefined : setNodeRef}
      style={{
        ...style,
        boxSizing: "border-box",
        transition,
        position: "relative",
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : undefined,
        minHeight:"70vh",
        height: "70vh",
        overflowY: "hidden",
      }}
      hover={isOverContainer}
      handleProps={{
        ...attributes,
        ...listeners,
      }}
      columns={columns}
      dataEtapa={dataEtapa}
      dataGeneral={dataGeneral}
      infoTablero={infoTablero}
      setData={setData}
      setItems={setItems}
      handleRemoveColumn={handleRemoveColumn}
      setCargando={setCargando}
      setMensaje={setMensaje}
      updateData={updateData}
      idjerarquia={idjerarquia}
      usuario={usuario}
      permisos={permisos}
      esPrimeraEtapa={esPrimeraEtapa}
      {...props}
    >
      {children}
    </Container>
  );
}

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: "0.5",
      },
    },
  }),
};

export const TRASH_ID = "void";
const PLACEHOLDER_ID = "placeholder";
const empty = [];

export function TableroContainer({
  adjustScale = false,
  itemCount = 3,
  cancelDrop,
  columns,
  handle = false,
  items: initialItems,
  containerStyle,
  coordinateGetter = multipleContainersCoordinateGetter,
  getItemStyles = () => ({}),
  wrapperStyle = () => ({}),
  minimal = false,
  modifiers,
  renderItem,
  strategy = verticalListSortingStrategy,
  trashable = false,
  vertical = false,
  scrollable,
  dataOriginal,
  info,
  idtablero,
  cargarTablero,
  idjerarquia,
  usuario,
  permisos,
}) {
  const [data, setData] = useState(dataOriginal);
  const [items, setItems] = useState(
    () =>
      initialItems ?? {
        A: ["A1", "A2", "A3"],
        B: createRange(itemCount, (index) => `B${index + 1}`),
        C: createRange(itemCount, (index) => `C${index + 1}`),
        D: createRange(itemCount, (index) => `D${index + 1}`),
      }
  );
  const [containers, setContainers] = useState(Object.keys(items));
  const [activeId, setActiveId] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState({ open: false });
  const lastOverId = useRef(null);
  const recentlyMovedToNewContainer = useRef(false);
  const isSortingContainer = activeId ? containers.includes(activeId) : false;

  const cerrarMensaje = () => setMensaje({ open: false });

  const obtenerEtapaIndex = (idetapa) => {
    let etapaEncontrada = data?.find((etapa) => etapa?.idetapa == idetapa);
    return data.indexOf(etapaEncontrada);
  }

  const obtenerTarjetaIndex = (idtarjeta) => {
    let tarjeta = null;
    data.map((etapa) => {
      let tarjetaFind = etapa.tarjetas.find(
        (tarjeta) => tarjeta.idtarjeta == idtarjeta
      );
      if (tarjetaFind) {
        tarjeta = tarjetaFind;
        return;
      }
    });

    let indexEtapa = obtenerEtapaIndex(tarjeta.idetapa);
    let etapa = data[indexEtapa];
    let indexTarjeta = etapa.tarjetas.indexOf(tarjeta);

    return { indexEtapa: indexEtapa, indexTarjeta: indexTarjeta };
  }

  const updateData = (elemento, nuevoValor, identificador) => {
    let auxData = data;
    // actualizamos toda la data
    if (elemento == 'all') {
      setData(nuevoValor);
      return
    }

    if (elemento == 'tarjeta') {
      let indexElementos = obtenerTarjetaIndex(identificador);
      auxData[indexElementos.indexEtapa].tarjetas[indexElementos.indexTarjeta] = nuevoValor;
      setData(auxData);
    }

    if (elemento == 'etapa') {
      let indexEtapa = obtenerEtapaIndex(identificador);
      auxData[indexEtapa] = nuevoValor;
      setData(auxData);
    }

  }

  /**
   * Custom collision detection strategy optimized for multiple containers
   *
   * - First, find any droppable containers intersecting with the pointer.
   * - If there are none, find intersecting containers with the active draggable.
   * - If there are no intersecting containers, return the last matched intersection
   *
   */
  const collisionDetectionStrategy = useCallback(
    (args) => {
      if (activeId && activeId in items) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (container) => container.id in items
          ),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, "id");

      if (overId != null) {
        if (overId === TRASH_ID) {
          // If the intersecting droppable is the trash, return early
          // Remove this if you're not using trashable functionality in your app
          return intersections;
        }

        if (overId in items) {
          const containerItems = items[overId];

          // If a container is matched and it contains items (columns 'A', 'B', 'C')
          if (containerItems.length > 0) {
            // Return the closest droppable within that container
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) =>
                  container.id !== overId &&
                  containerItems.includes(container.id)
              ),
            })[0]?.id;
          }
        }

        lastOverId.current = overId;

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, items]
  );
  const [clonedItems, setClonedItems] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter,
    })
  );
  const findContainer = (id) => {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find((key) => items[key].includes(id));
  };

  const getIndex = (id) => {
    const container = findContainer(id);

    if (!container) {
      return -1;
    }

    const index = items[container].indexOf(id);

    return index;
  };

  const onDragCancel = () => {
    if (clonedItems) {
      // Reset items to their original state in case items have been
      // Dragged across containers
      setItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  };

  function handleAddColumn() {
    serviciosTablero.crearEtapa(
      (status, mensaje, response) => {
        const newContainerId = "e" + response.idetapa;
        let etapaTemplate = {
          indexEtapa: newContainerId,
          idetapa: response.idetapa,
          titulo: "Sin título",
          nombre: "Sin título",
          estatusGeneral: "0",
          color: "",
          tarjetas: [],
        };
        setMensaje({ open: true, status: status, value: mensaje });
        unstable_batchedUpdates(() => {
          setData([...data, etapaTemplate]);
          setContainers((containers) => [...containers, newContainerId]);

          setItems((items) => ({
            ...items,
            [newContainerId]: [],
          }));
        });
      },
      setCargando,
      { idtablero: idtablero, nombre: "Sin título", estatusGeneral: "0" }
    );
  }

  function handleRemoveColumn(dataEtapa) {
    let columnToRemove = Object.keys(items);
    let tempItems = items;
    columnToRemove = columnToRemove.find((key) => key == dataEtapa.indexEtapa);

    serviciosTablero.eliminarEtapa(
      (status, mensaje, response) => {
        setMensaje({ open: true, status: status, value: mensaje });
        if (status > 0) {
          delete tempItems[columnToRemove];
          unstable_batchedUpdates(() => {
            //setData([...data, etapaTemplate]);
            setItems(tempItems);
            setContainers(Object.keys(tempItems));
          });
        }
      },
      setCargando,
      { idetapa: dataEtapa.idetapa }
    );
  }

  const ordernarTablero = (keys, objectKeys) => {
    let nuevaData = keysToData(objectKeys, data);
    setData(nuevaData);
    serviciosTablero.ordernarTablero(
      (data) => {},
      () => {},
      { data: keys, tipoTablero: info?.idtipo }
    );
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [items]);

  return (
    <Page
      title="Tablero"
      className={{}}
      mensaje={{ ...mensaje, cerrar: cerrarMensaje }}
      cargando={cargando}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetectionStrategy}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
        onDragStart={({ active }) => {
          setActiveId(active.id);
          setClonedItems(items);
        }}
        onDragOver={({ active, over }) => {
          const overId = over?.id;

          if (overId == null || overId === TRASH_ID || active.id in items) {
            return;
          }

          const overContainer = findContainer(overId);
          const activeContainer = findContainer(active.id);

          if (!overContainer || !activeContainer) {
            return;
          }

          if (activeContainer !== overContainer) {
            setItems((items) => {
              const activeItems = items[activeContainer];
              const overItems = items[overContainer];
              const overIndex = overItems.indexOf(overId);
              const activeIndex = activeItems.indexOf(active.id);

              let newIndex;

              if (overId in items) {
                newIndex = overItems.length + 1;
              } else {
                const isBelowOverItem =
                  over &&
                  active.rect.current.translated &&
                  active.rect.current.translated.top >
                    over.rect.top + over.rect.height;

                const modifier = isBelowOverItem ? 1 : 0;

                newIndex =
                  overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
              }

              recentlyMovedToNewContainer.current = true;

              return {
                ...items,
                [activeContainer]: items[activeContainer].filter(
                  (item) => item !== active.id
                ),
                [overContainer]: [
                  ...items[overContainer].slice(0, newIndex),
                  items[activeContainer][activeIndex],
                  ...items[overContainer].slice(
                    newIndex,
                    items[overContainer].length
                  ),
                ],
              };
            });
          }
        }}
        onDragEnd={({ active, over }) => {
          let orderOnSameEtapa = false;
          if (active.id in items && over?.id) {
            setContainers((containers) => {
              const activeIndex = containers.indexOf(active.id);
              const overIndex = containers.indexOf(over.id);
              let newArray = arrayMove(containers, activeIndex, overIndex);
              let newItems = {};
              newArray.forEach((container) => {
                newItems[container] = [];
              });

              ordernarTablero(JSON.stringify(newItems), newItems);
              orderOnSameEtapa = true;

              return newArray;
            });
          }

          const activeContainer = findContainer(active.id);

          if (!activeContainer) {
            setActiveId(null);
            return;
          }

          const overId = over?.id;

          if (overId == null) {
            setActiveId(null);
            return;
          }

          if (overId === TRASH_ID) {
            setItems((items) => {
              let newItems = {
                ...items,
                [activeContainer]: items[activeContainer].filter(
                  (id) => id !== activeId
                ),
              };
              ordernarTablero(JSON.stringify(newItems), newItems);
              orderOnSameEtapa = true;
              return newItems;
            });
            setActiveId(null);
            return;
          }

          if (overId === PLACEHOLDER_ID) {
            const newContainerId = getNextContainerId();
            unstable_batchedUpdates(() => {
              setContainers((containers) => [...containers, newContainerId]);
              setItems((items) => {
                let newItems = {
                  ...items,
                  [activeContainer]: items[activeContainer].filter(
                    (id) => id !== activeId
                  ),
                  [newContainerId]: [active.id],
                };
                ordernarTablero(JSON.stringify(newItems), newItems);
                orderOnSameEtapa = true;
                return newItems;
              });
              setActiveId(null);
            });
            return;
          }

          const overContainer = findContainer(overId);

          if (overContainer) {
            const activeIndex = items[activeContainer].indexOf(active.id);
            const overIndex = items[overContainer].indexOf(overId);

            if (activeIndex !== overIndex) {
              setItems((items) => {
                let newItems = {
                  ...items,
                  [overContainer]: arrayMove(
                    items[overContainer],
                    activeIndex,
                    overIndex
                  ),
                };
                ordernarTablero(JSON.stringify(newItems), newItems);
                orderOnSameEtapa = true;
                return newItems;
              });
            }
          }

          if (!orderOnSameEtapa) ordernarTablero(JSON.stringify(items), items);
          setActiveId(null);
        }}
        cancelDrop={cancelDrop}
        onDragCancel={onDragCancel}
        modifiers={modifiers}
      >
        {/* Esta parte es para editar el ancho del card */}
        
        <Box
          style={{
            display: "inline-grid",
            boxSizing: "border-box",
            padding: 20,
            gridAutoFlow: vertical ? "row" : "column",
            width: "100px", // Fija el ancho
            maxWidth: "100%", // Evita que se desborde en pantallas pequeñas
            height: "auto",
            padding: "16px",
            
          }}
        >
          <SortableContext
            items={[...containers, PLACEHOLDER_ID]}
            strategy={
              vertical
                ? verticalListSortingStrategy
                : horizontalListSortingStrategy
            }
          >
            {containers.map((containerId, indexArray) => (
              <DroppableContainer
                key={containerId}
                id={containerId}
                label={
                  data.find((etapa) => etapa.indexEtapa == containerId).titulo
                }
                columns={columns}
                items={items[containerId]}
                scrollable={scrollable}
                style={containerStyle}
                unstyled={minimal}
                onRemove={() => handleRemove(containerId)}
                dataEtapa={data.find(
                  (etapa) => etapa.indexEtapa == containerId
                )}
                dataGeneral={data}
                setData={setData}
                setItems={setItems}
                handleRemoveColumn={handleRemoveColumn}
                infoTablero={info}
                setCargando={setCargando}
                setMensaje={setMensaje}
                updateData={updateData}
                idjerarquia={idjerarquia}
                usuario={usuario}
                permisos={permisos}
                esPrimeraEtapa={indexArray === 0}
              >
                <SortableContext items={items[containerId]} strategy={strategy}>
                  {items[containerId].map((value, index) => {
                    return (
                      <SortableItem
                        disabled={isSortingContainer}
                        key={value}
                        id={value}
                        index={index}
                        handle={handle}
                        style={getItemStyles}
                        wrapperStyle={wrapperStyle}
                        renderItem={renderItem}
                        containerId={containerId}
                        getIndex={getIndex}
                        dataEtapa={data.find(
                          (etapa) => etapa.indexEtapa == containerId
                        )}
                        dataGeneral={data}
                        infoTablero={info}
                        setData={setData}
                        setCargando={setCargando}
                        setMensaje={setMensaje}
                        cargarTablero={cargarTablero}
                        updateData={updateData}
                        idjerarquia={idjerarquia}
                        usuario={usuario}
                        permisos={permisos}
                      />
                    );
                  })}
                </SortableContext>
              </DroppableContainer>
            ))}
            {info?.idtipo == 2 ? ( // Si es tablero de avances no mostrar opcion
              ""
            ) : (
              <div>
                {/* <Button
                  style={{
                    width: "100%",
                    backgroundColor: "#696868",
                    color: "#FFFDFD",
                  }}
                  onClick={handleAddColumn}
                >
                  <Add />
                  Agregar Etapa
                </Button> */}
              </div>
            )}
          </SortableContext>
        </Box>
        {createPortal(
          <DragOverlay adjustScale={adjustScale} dropAnimation={dropAnimation}>
            {activeId
              ? containers.includes(activeId)
                ? renderContainerDragOverlay(activeId)
                : renderSortableItemDragOverlay(activeId)
              : null}
          </DragOverlay>,
          document.body
        )}
        {trashable && activeId && !containers.includes(activeId) ? (
          <Trash id={TRASH_ID} />
        ) : null}
      </DndContext>
    </Page>
  );

  function renderSortableItemDragOverlay(id) {
    return (
      <Item
        value={id}
        handle={handle}
        style={getItemStyles({
          containerId: findContainer(id),
          overIndex: -1,
          index: getIndex(id),
          value: id,
          isSorting: true,
          isDragging: true,
          isDragOverlay: true,
          overflowY: "auto",
          
        })}
        color={getColor(id)}
        wrapperStyle={wrapperStyle({ index: 0 })}
        renderItem={renderItem}
        infoTablero={info}
        cargarTablero={cargarTablero}
        updateData={updateData}
        idjerarquia={idjerarquia}
        usuario={usuario}
        permisos={permisos}
        dragOverlay
      />
    );
  }

  function renderContainerDragOverlay(containerId) {
    return (
      <Container
        label={"Ordenando etapa..."}
        dataEtapa={data.find((etapa) => etapa.indexEtapa == containerId)}
        dataGeneral={data}
        infoTablero={info}
        setData={setData}
        setItems={setItems}
        handleRemoveColumn={handleRemoveColumn}
        columns={columns}
        style={{
          height: "100%",
        }}
        shadow
        unstyled={false}
        updateData={updateData}
        idjerarquia={idjerarquia}
        usuario={usuario}
        permisos={permisos}
        esPrimeraEtapa = {containers.indexOf(containers[containerId]) === 0}
      >
        {items[containerId].map((item, index) => (
          <Item
            key={item}
            value={item}
            handle={handle}
            style={getItemStyles({
              containerId,
              overIndex: -1,
              index: getIndex(item),
              value: item,
              isDragging: false,
              isSorting: false,
              isDragOverlay: false,
            })}
            color={getColor(item)}
            wrapperStyle={wrapperStyle({ index })}
            infoTablero={info}
            renderItem={renderItem}
            cargarTablero={cargarTablero}
            updateData={updateData}
            idjerarquia={idjerarquia}
            usuario={usuario}
            permisos={permisos}
          />
        ))}
      </Container>
    );
  }

  function handleRemove(containerID) {
    setContainers((containers) =>
      containers.filter((id) => id !== containerID)
    );
  }

  function getNextContainerId() {
    const containerIds = Object.keys(items);
    const lastContainerId = containerIds[containerIds.length - 1];

    return String.fromCharCode(lastContainerId.charCodeAt(0) + 1);
  }
}

function getColor(id) {
  switch (String(id)[0]) {
    case "A":
      return "#7193f1";
    case "B":
      return "#ffda6c";
    case "C":
      return "#00bcd4";
    case "D":
      return "#ef769f";
  }

  return undefined;
}

function Trash(id) {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "fixed",
        left: "50%",
        // marginLeft: -150,
        bottom: 20,
        width: 300,
        height: 60,
        borderRadius: 5,
        border: "1px solid",
        borderColor: isOver ? "red" : "#DDD",
      }}
    >
      Drop here to delete
    </div>
  );
}

function SortableItem({
  disabled,
  id,
  index,
  key,
  handle,
  renderItem,
  style,
  containerId,
  getIndex,
  wrapperStyle,
  dataEtapa,
  dataGeneral,
  infoTablero,
  setData,
  setCargando,
  setMensaje,
  cargarTablero,
  updateData,
  idjerarquia,
  usuario,
  permisos,
}) {
  const {
    setNodeRef,
    setActivatorNodeRef,
    listeners,
    isDragging,
    isSorting,
    over,
    overIndex,
    transform,
    transition,
  } = useSortable({
    id,
  });
  const mounted = useMountStatus();
  const mountedWhileDragging = isDragging && !mounted;

  const getDataTarjeta = () => {
    let tarjeta = null;
    dataGeneral.map((etapa) => {
      let tarjetaFind = etapa.tarjetas.find(
        (tarjeta) => tarjeta.indexTarjeta == id
      );
      if (tarjetaFind) {
        tarjeta = tarjetaFind;
        return;
      }
    });

    return tarjeta;
  };

  return (
    <Item
      ref={disabled ? undefined : setNodeRef}
      value={id}
      dragging={isDragging}
      sorting={isSorting}
      handle={handle}
      handleProps={handle ? { ref: setActivatorNodeRef } : undefined}
      index={index}
      wrapperStyle={wrapperStyle({ index })}
      style={style({
        index,
        value: id,
        isDragging,
        isSorting,
        overIndex: over ? getIndex(over.id) : overIndex,
        containerId,
      })}
      color={getColor(id)}
      transition={transition}
      transform={transform}
      fadeIn={mountedWhileDragging}
      listeners={listeners}
      renderItem={renderItem}
      dataEtapa={dataEtapa}
      dataTarjeta={getDataTarjeta()}
      dataGeneral={dataGeneral}
      infoTablero={infoTablero}
      setData={setData}
      setCargando={setCargando}
      setMensaje={setMensaje}
      cargarTablero={cargarTablero}
      updateData={updateData}
      idjerarquia={idjerarquia}
      usuario={usuario}
      permisos={permisos}
    />
  );
}

function useMountStatus() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setIsMounted(true), 500);

    return () => clearTimeout(timeout);
  }, []);

  return isMounted;
}

export default TableroContainer;
